import { format } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

const TIME_ZONE = 'Europe/Berlin'

/**
 * Determines if a given date is during Daylight Saving Time (DST) in 'Europe/Berlin' time zone.
 *
 * @param date The date to check.
 * @returns True if the date is during DST in 'Europe/Berlin' time zone, false otherwise.
 */
export function isDST(date: Date): boolean {
  const berlinHour = toZonedTime(date, TIME_ZONE).getHours()
  const utcHour = date.getUTCHours()
  return (berlinHour - utcHour + 24) % 24 === 2
}

/**
 * Formats a date into a day-month-year string according to the 'Europe/Berlin' time zone.
 *
 * @param date The date to format.
 * @returns The formatted date string in 'Europe/Berlin' time zone format.
 */
export function formatDate(date: Date): string {
  const zonedDate = toZonedTime(date, TIME_ZONE)
  return format(zonedDate, 'dd.MM.yyyy')
}

/**
 * Finds the date of the transition day when DST ends for a given year in 'Europe/Berlin' time zone.
 * DST typically ends on the last Sunday of October.
 *
 * @param year The year for which to find the transition day.
 * @returns The date string of the transition day in 'Europe/Berlin' time zone.
 */
function findFallBackTransitionDay(year: number): string {
  const date = new Date(year, 9, 31) // Initialize to October 31st
  while (date.getDay() !== 0) {
    // Loop back to the last Sunday
    date.setDate(date.getDate() - 1)
  }
  return format(date, 'yyyy-MM-dd')
}

/**
 * Formats a date into a time string according to the 'Europe/Berlin' time zone.
 * Special handling is included for the DST fall-back transition in 'Europe/Berlin'.
 *
 * @param date The date to format.
 * @returns The formatted time string in 'Europe/Berlin' time zone.
 */
export function formatTime(date: Date): string {
  const zonedDate = toZonedTime(date, TIME_ZONE)
  const hour = zonedDate.getHours()
  const minute = zonedDate.getMinutes()

  const transitionDay = findFallBackTransitionDay(zonedDate.getFullYear())

  if (format(zonedDate, 'yyyy-MM-dd') === transitionDay && hour === 2) {
    // Handle the ambiguity caused by the fall-back transition at 2 AM in 'Europe/Berlin'.
    const minutesStr = minute.toString().padStart(2, '0')
    return isDST(date) ? `2A:${minutesStr}` : `2B:${minutesStr}`
  }

  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
}
